import React from 'react';
import * as Yup from 'yup';
import {
  Box,
  Button,
  CircularProgress,
  Skeleton,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Formik, Form } from 'formik';
import ReCAPTCHA from 'react-google-recaptcha';
import TermsAndConditionsLink from '../../components/TermsAndConditionsLink';
import AlertDialog from '../../components/AlertDialog';
import { setRut } from '../../utils/helpers';
import analytics from '../../utils/analytics';
import type { Partner } from '../../components/LeadFormIndex/types';
import { rutValidationSchema } from '../preloan/v3/validationSchemas';
import CreditLineScreen from './CreditLineScreen';
import CreditLineErrorScreen from './CreditLineErrorScreen';
import postBNPLRegisterPreApprove, { BNPLRegisterPreApproveResponse } from '../../queries/bnpl';
import CreditLineRejectedScreen from './CreditLineRejectedScreen';

type FormValues = {
  rut: string,
  email: string,
};

type RegistrationFormProps = {
  partner: Partner,
  origin?: string,
};

type CaptchaOutcome = 'success' | 'error_503' | 'other_errors' | 'failed';
type BnplEvaluationRequest = {
  idNumber: string,
  email: string,
  captchaToken: string,
  origin?: string,
};

const MailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const bnplValidationSchema = rutValidationSchema.shape({
  email: Yup.string()
    .required('Por favor ingresa tu email')
    .matches(MailRegex, 'Por favor ingresa un email válido'),
});

export default function RegistrationForm({ partner, origin }: RegistrationFormProps) {
  const theme = useTheme();
  const [checked, setChecked] = React.useState(false);
  const [closeButtonDisabled, setCloseButtonDisabled] = React.useState(false);
  const [triggerClose, setTriggerClose] = React.useState(false);
  const [resultScreen, setResultScreen] = React.useState<CaptchaOutcome | null>(null);
  const [isCaptchaLoading, setIsCaptchaLoading] = React.useState(false);
  const [isCaptchaReady, setIsCaptchaReady] = React.useState(false);
  const [evaluationResponse, setEvaluationResponse] = React.useState<BNPLRegisterPreApproveResponse['data'] | null>(null);
  const recaptchaRef = React.useRef<ReCAPTCHA>(null);
  const captchaLoadingTimeoutRef = React.useRef<number | null>(null);

  React.useEffect(() => () => {
    if (captchaLoadingTimeoutRef.current) {
      window.clearTimeout(captchaLoadingTimeoutRef.current);
    }
  }, []);

  React.useEffect(() => {
    analytics.page('BNPL - REGISTRO', 'INGRESO RUT');
  }, []);

  const mapEvaluationResponseToScreen = (response: { accepted: boolean | 'error', errorCode?: number, isRegistered?: boolean}): CaptchaOutcome => {
    switch (response.accepted) {
      case true:
        return 'success';
      case false:
        return 'failed';
      default: {
        if (response.errorCode === 503) {
          return 'error_503';
        }
        return 'other_errors';
      }
    }
  };

  const recaptchaOnChange = async (token: string | null, rut: string, email: string) => {
    if (token) {
      setCloseButtonDisabled(true);
      setIsCaptchaLoading(true);
      if (captchaLoadingTimeoutRef.current) {
        window.clearTimeout(captchaLoadingTimeoutRef.current);
      }

      const requestPayload: BnplEvaluationRequest = {
        idNumber: rut,
        email,
        captchaToken: token,
        origin,
      };
      let nextScreen: CaptchaOutcome | null = null;
      try {
        const evaluationRes = await postBNPLRegisterPreApprove(requestPayload, token);
        nextScreen = mapEvaluationResponseToScreen(evaluationRes.data);
        setEvaluationResponse(evaluationRes.data);
        if (evaluationRes.data?.identificationId) {
          analytics.identify(evaluationRes.data?.identificationId);
        }
      } catch (error) {
        nextScreen = mapEvaluationResponseToScreen({ accepted: 'error', errorCode: error as number });
      }

      setIsCaptchaLoading(false);
      setCloseButtonDisabled(false);
      setTriggerClose(true);
      setResultScreen(nextScreen);
      window.setTimeout(() => setTriggerClose(false), 0);
    }
  };

  const handleOpenRecaptchaDialog = () => {
    setCloseButtonDisabled(false);
    setTriggerClose(false);
    setIsCaptchaLoading(false);
    recaptchaRef.current?.reset();
  };

  if (resultScreen === 'success' && evaluationResponse) {
    return (
      <CreditLineScreen
        evaluationResponse={evaluationResponse}
      />
    );
  }

  if (resultScreen === 'failed' && evaluationResponse) {
    return (
      <CreditLineRejectedScreen
        hardFilters={evaluationResponse.hardFilters}
        isRegistered={evaluationResponse.isRegistered}
      />
    );
  }

  if (resultScreen === 'error_503') {
    return (
      <CreditLineErrorScreen
        message="No podemos evaluar tu caso, por favor contáctanos"
        pageName="ERROR EVALUACION BNPL"
      />
    );
  }

  if (resultScreen === 'other_errors') {
    return (
      <CreditLineErrorScreen
        message="Ha ocurrido un error, por favor contáctanos"
        pageName="ERROR"
      />
    );
  }

  return (
    <Formik<FormValues>
      initialValues={{ rut: '', email: '' }}
      validationSchema={bnplValidationSchema}
      validateOnMount
      onSubmit={() => {}}
    >
      {({
        values, handleBlur, handleChange, touched, errors, setFieldValue, isValid,
      }) => (
        <Form>
          <Typography
            component="div"
            style={{ opacity: 0.5, marginBottom: 10 }}
            variant="body2"
            align="center"
            gutterBottom
          >
            Por favor ingresa tu RUT y correo electrónico para conocer tu
            {' '}
            <b>poder de compra</b>
            {' '}
            disponible en banca.me
          </Typography>
          <TextField
            focused
            size="small"
            variant="outlined"
            autoComplete="off"
            placeholder="RUT"
            inputProps={{ style: { textAlign: 'center' } }}
            id="rut"
            name="rut"
            data-pw="preloan-rut-input"
            autoFocus
            error={touched.rut && Boolean(errors.rut)}
            helperText={touched.rut && errors.rut ? errors.rut : '  '}
            FormHelperTextProps={{
              style: {
                textAlign: 'center',
                height: 5,
              },
            }}
            value={values.rut}
            onChange={(e) => setRut(setFieldValue, e.target.value)}
            onBlur={(e) => {
              setRut(setFieldValue, values.rut);
              handleBlur(e);
            }}
            fullWidth
            sx={{ marginBottom: 2 }}
          />
          <TextField
            size="small"
            variant="outlined"
            autoComplete="off"
            placeholder="Correo electrónico"
            inputProps={{ style: { textAlign: 'center' } }}
            id="email"
            name="email"
            type="email"
            error={touched.email && Boolean(errors.email)}
            helperText={touched.email && errors.email ? errors.email : '  '}
            FormHelperTextProps={{
              style: {
                textAlign: 'center',
                height: 5,
              },
            }}
            value={values.email}
            onChange={handleChange}
            onBlur={handleBlur}
            fullWidth
            sx={{ marginBottom: 2 }}
          />
          <TermsAndConditionsLink
            origin="bnpl"
            checked={checked}
            handleSetChecked={() => setChecked((prevValue) => !prevValue)}
            partner={partner}
            formControlLabelProps={{
              backgroundColor: theme.palette.background.default,
              padding: '5px 0px',
              borderRadius: 20,
            }}
            additionalText=""
          />
          <div style={{ textAlign: 'center' }}>
            <AlertDialog
              pageCall={{
                category: 'BNPL - REGISTRO',
                name: 'VERIFICACION DE SEGURIDAD',
              }}
              hideCloseButton
              disabled={!checked || !isValid}
              hideSubmitButton
              hideLogo
              closeButtonDisabled={closeButtonDisabled}
              triggerClose={triggerClose}
              dialogTitle={(
                <Typography
                  variant="h6"
                  component="div"
                  align="center"
                  gutterBottom
                  fontWeight="bold"
                >
                  Verificación de seguridad
                </Typography>
              )}
              dialogContent={(
                <Box onClickCapture={() => setCloseButtonDisabled(true)}>
                  <Box sx={{
                    display: 'flex', justifyContent: 'center', marginTop: 2.5, position: 'relative',
                  }}
                  >
                    {!isCaptchaReady && (
                      <Skeleton
                        variant="rectangular"
                        width={304}
                        height={78}
                        sx={{ borderRadius: 1 }}
                      />
                    )}
                    <Box
                      sx={{
                        position: isCaptchaReady ? 'static' : 'absolute',
                        left: isCaptchaReady ? 'auto' : '50%',
                        transform: isCaptchaReady ? 'none' : 'translateX(-50%)',
                        visibility: isCaptchaReady ? 'visible' : 'hidden',
                      }}
                    >
                      <ReCAPTCHA
                        onChange={(token) => recaptchaOnChange(token, values.rut, values.email)}
                        asyncScriptOnLoad={() => setIsCaptchaReady(true)}
                        ref={recaptchaRef}
                        sitekey={process.env.REACT_APP_CAPTCHA_PLRV2 as string}
                      />
                    </Box>
                  </Box>
                  {isCaptchaLoading && (
                    <Box sx={{
                      display: 'flex', justifyContent: 'center', marginTop: 2, flexDirection: 'column',
                    }}
                    >
                      <CircularProgress size={28} />
                      <Typography marginTop={1} component="div" variant="caption" align="center" gutterBottom>Verificando...</Typography>
                    </Box>
                  )}
                </Box>
              )}
              button={(
                <Button
                  disabled={!checked || !isValid}
                  type="button"
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={handleOpenRecaptchaDialog}
                  sx={{ marginBottom: 2 }}
                >
                  Ver poder de compra
                </Button>
              )}
            />
          </div>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: 1,
            }}
          >
            <img
              src="https://assets.banca.me/bancame_logo_dark.svg"
              alt="Banca.me"
              style={{ width: 70, height: 'auto', marginTop: 5 }}
            />
          </Box>
        </Form>
      )}
    </Formik>
  );
}

RegistrationForm.defaultProps = {
  origin: undefined,
};
